import React, { ReactNode, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { RootState } from 'store';

import generateYupSchema from '~libs/yupValidation';

import ConfirmDialog from '~components/ConfirmDialog';
import ErrorResponse from '~components/ErrorResponse';

import Footer from './Footer';
import { Step, StepConfirmConfig } from './props';
import Stepper from './Stepper';
import Steps from './Steps';

/**
 * Props for the WizardForm component.
 * @property steps - Array of form steps, each containing a FieldProps structure.
 * @property onStepChange - Callback triggered after a step change occurs.
 * @property beforeStepChange - Callback triggered before a step changes.
 * @property onComplete - Async callback triggered as the form is completing.
 * @property onCancel - Async callback triggered when the form is cancelled.
 * @property renderFooterButtons - Custom renderer for footer navigation buttons.
 * @property renderFormBody - Function to wrap the form body with additional components.
 */
interface ComponentProps<T = any> {
  steps: Step[];
  onStepChange?: (step: number, stepValues: T) => void;
  onComplete?: ((formData: T) => Promise<boolean>) | ((formData: T) => void);
  onCancel?: () => void;
  beforeStepChange?: (step: number, data: T) => Promise<boolean>;
  renderFooterButtons?: (
    handleNext: () => void,
    handlePrev: () => void,
    currentStep: number,
    stepCount: number,
  ) => React.ReactNode;
  initialValues?: T;
  renderFormBody?: (formBody: ReactNode) => ReactNode;
  errorSelector?: (state: RootState) => string | null | undefined;
  bodyClassname?: string;
  stepperClassName?: string;
  submitCaption?: string;
}

/**
 * WizardForm component for managing multi-step forms.
 *
 * @param props
 * @returns The rendered WizardForm component.
 */
export const Form = <T extends Record<string, any> = Record<string, any>>({
  steps,
  onStepChange,
  renderFooterButtons,
  renderFormBody,
  beforeStepChange,
  onComplete,
  onCancel,
  initialValues = {} as T,
  errorSelector,
  bodyClassname,
  stepperClassName,
  submitCaption,
}: ComponentProps<T>) => {
  let errorResponse;
  if (errorSelector) errorResponse = useAppSelector(errorSelector);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const formikRef = useRef<any>(null);

  const stepCount = steps.length;
  const isStepValid = invalidFields.length === 0;
  const step = steps[currentStep];
  const fieldDefinitions = currentStep < stepCount - 1 ? step.fields : {};
  const validationSchema = useMemo(() => generateYupSchema(fieldDefinitions), [fieldDefinitions]);
  const canSubmit = stepCount === steps.length && invalidFields.length < 1;

  const getConfirmConfig = (): StepConfirmConfig | null => {
    const { confirmNext } = step;
    if (!confirmNext) return null;
    if (typeof confirmNext === 'function') {
      return confirmNext({ values: formikRef.current?.values, currentStep });
    }
    return confirmNext;
  };

  const proceedToNextStep = async () => {
    const canProceed = beforeStepChange ? await beforeStepChange(currentStep, formikRef.current?.values as T) : true;
    if (canProceed) {
      const nextStep = Math.min(currentStep + 1, stepCount - 1);
      setCurrentStep(nextStep);
      onStepChange?.(nextStep, formikRef.current?.values);
    }
  };

  const handleNext = async () => {
    const isValid = await validateStep();
    if (!isValid) return;

    const confirmConfig = getConfirmConfig();
    if (confirmConfig) {
      setShowConfirmDialog(true);
    } else {
      await proceedToNextStep();
    }
  };

  const handleConfirmDialogClose = async (confirmed: boolean) => {
    setShowConfirmDialog(false);
    if (confirmed) {
      await proceedToNextStep();
    }
  };

  const handlePrev = async () => {
    setInvalidFields([]);
    const canProceed = beforeStepChange ? await beforeStepChange(currentStep, formikRef.current?.values as T) : true;
    if (canProceed) {
      const prevStep = Math.max(currentStep - 1, 0);
      setCurrentStep(prevStep);
      onStepChange?.(prevStep, formikRef.current?.values);
    }
  };

  const handleCancel = onCancel
    ? async () => {
        onCancel?.();
        setInvalidFields([]);
        formikRef.current?.resetForm();
      }
    : undefined;

  const validateStep = async (): Promise<boolean> => {
    const stepSchema = generateYupSchema(step?.fields);
    const values = formikRef.current?.values;

    try {
      await stepSchema.validate(values, { abortEarly: false });
      setInvalidFields([]);
      return true;
    } catch (error: any) {
      if (error.inner) {
        const errors = error.inner.map(({ path }: any) => path);
        setInvalidFields(errors);
      }
      return false;
    }
  };

  const handleYupValidation = async (values: any) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      setInvalidFields([]);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = err.inner.map(({ path }) => path).filter((path): path is string => !!path);
        setInvalidFields(errors);
      }
    }
  };

  const submitForm = async (setSubmitting: (isSubmitting: boolean) => void, values: T) => {
    try {
      if (canSubmit) {
        setSubmitting(true);
        // Strip UI-only fields (isAttribute: false) so they are not sent to the API
        const uiOnlyKeys = new Set(
          steps
            .flatMap((step) => Object.entries(step.fields))
            .filter(([, field]) => field.isAttribute === false)
            .map(([key]) => key),
        );
        const submitValues = uiOnlyKeys.size
          ? (Object.fromEntries(Object.entries(values).filter(([key]) => !uiOnlyKeys.has(key))) as T)
          : values;
        await onComplete?.(submitValues);
        setSubmitting(false);
      }
    } catch (error) {
      // Console error introduced when unknown type of error thrown.
      // This should be moved to page statistics/logging when implemented
      console.error('Unknown error occurred:', error);
    }
  };

  const formBody = (
    <div className={bodyClassname}>
      <ErrorResponse message={errorResponse} />
      <Formik<T>
        initialValues={initialValues}
        validate={handleYupValidation}
        innerRef={formikRef}
        validateOnMount={false}
        // Re-validate on change only once the wizard step has hit a validation failure —
        // this clears stale invalid-field highlights as the user fixes them.
        validateOnChange={invalidFields.length > 0}
        validateOnBlur={false}
        onSubmit={(values, { setSubmitting }) => submitForm(setSubmitting, values)}
      >
        {(context) => {
          const resolvedShowPrevButton =
            typeof step.showPrevButton === 'function'
              ? step.showPrevButton({
                  values: context.values,
                  currentStep,
                  stepCount,
                  isStepValid,
                })
              : (step.showPrevButton ?? true);

          const resolvedShowNextButton =
            typeof step.showNextButton === 'function'
              ? step.showNextButton({
                  values: context.values,
                  currentStep,
                  stepCount,
                  isStepValid,
                })
              : (step.showNextButton ?? true);

          const resolvedDisableNext =
            typeof step.disableNext === 'function'
              ? step.disableNext({
                  values: context.values,
                  currentStep,
                  stepCount,
                  isStepValid,
                })
              : (step.disableNext ?? false);

          return (
            <FormikForm className={`wizard-form-content step-${currentStep}`}>
              <Steps
                authTag=""
                context={context}
                currentStep={currentStep}
                steps={steps}
                stepCount={stepCount}
                invalidFields={invalidFields}
              />

              <Footer
                currentStep={currentStep}
                handleNext={handleNext}
                handlePrev={handlePrev}
                isStepValid={isStepValid}
                renderFooterButtons={renderFooterButtons}
                stepCount={stepCount}
                isSubmitting={context.isSubmitting}
                handleSubmit={context.handleSubmit}
                handleCancel={handleCancel}
                showPrevButton={resolvedShowPrevButton}
                prevButtonCaption={step?.prevButtonCaption}
                showNextButton={resolvedShowNextButton}
                nextButtonCaption={step?.nextButtonCaption}
                disableNext={resolvedDisableNext}
                submitCaption={submitCaption}
              />

              <ConfirmDialog
                isOpen={showConfirmDialog}
                onClose={handleConfirmDialogClose}
                messages={getConfirmConfig()?.messages}
                useList={Array.isArray(getConfirmConfig()?.messages)}
              />
            </FormikForm>
          );
        }}
      </Formik>
    </div>
  );

  return (
    <div className="wizard-form">
      <Stepper currentStep={currentStep} steps={steps} className={stepperClassName} />
      {renderFormBody ? renderFormBody(formBody) : formBody}
    </div>
  );
};

export default Form;
